import * as Sentry from "@sentry/react";

/**
 * Sentry initialisation.
 *
 * This file is imported as the very first statement in src/main.tsx so that
 * Sentry starts before React renders.  It must never be imported more than
 * once — the import system guarantees that.
 *
 * DSN and environment come from Vite env vars so no secret is ever hard-coded
 * in source.  When VITE_SENTRY_DSN is absent (local dev without the var set)
 * the SDK is disabled and produces no noise.
 *
 * ── Cookie consent ──────────────────────────────────────────────────────────
 * diagnosticsAllowed() is defined INLINE here intentionally.
 * It must NOT be imported from CookiePreferences.tsx (a React component).
 * Importing a component module here would pull the entire legal component
 * subtree into the synchronous startup bundle, defeating code-splitting.
 */
const COOKIE_PREF_KEY = "mbaay_cookie_preferences";

/**
 * Returns true only when the user has explicitly accepted optional diagnostics.
 * Reads from localStorage — safe to call synchronously at module load time.
 */
const diagnosticsAllowed = (): boolean =>
  localStorage.getItem(COOKIE_PREF_KEY) === "accepted";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,

  // Use an explicit env var if provided; fall back to Vite's built-in MODE
  // so dev/production are always distinguished even without the extra var.
  environment:
    (import.meta.env.VITE_SENTRY_ENV as string | undefined) ??
    import.meta.env.MODE,

  integrations: [
    // Instruments page loads, route changes, and fetch/XHR for performance.
    // Works with React Router v7 createBrowserRouter automatically via
    // the History API — no wrapper component needed.
    Sentry.browserTracingIntegration(),

    // Session Replay — captures video-like replays on errors.
    // All text and media are masked/blocked to protect sensitive user data.
    // replaysSessionSampleRate is set to 0: normal sessions are NOT recorded
    // (reduces Sentry quota usage and eliminates ERR_BLOCKED_BY_CLIENT noise
    // from ad blockers on non-error sessions). Error sessions are still
    // captured at 100% so faults remain diagnosable.
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  // Capture all traces in dev; 10 % in production to keep quota reasonable.
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

  // 0 % of normal sessions recorded (reduces noise & quota).
  // 100 % of sessions that contain an error are still captured.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Disable the SDK entirely when no DSN is configured (local dev without a
  // Sentry project) OR when the user has not opted into diagnostics.
  enabled: !!import.meta.env.VITE_SENTRY_DSN && diagnosticsAllowed(),

  // Filter out noisy browser-extension and third-party errors.
  beforeSend(event) {
    // Ignore errors thrown by browser extensions (chrome-extension:// etc.)
    const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
    const hasExtensionFrame = frames.some(
      (f) =>
        f.filename?.startsWith("chrome-extension://") ||
        f.filename?.startsWith("moz-extension://"),
    );
    if (hasExtensionFrame) return null;

    return event;
  },
});
